import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { formatDateForApi, parseDateFromApi } from '../core/utils/date.utils';

export interface ControlDusFilters {
    clienteId?: number | null;
    temporadaId?: number | null;
    nave?: string | null;
    dateFrom?: Date | null;
    dateTo?: Date | null;
    referenciaCliente?: string;
    contenedor?: string;
    dus?: string;
    booking?: string;
    etapa?: string | null;
    estado?: string | null;
}

export interface ControlDusPagination {
    page: number;
    limit: number;
}

export interface ControlDusState {
    filters: ControlDusFilters;
    pagination: ControlDusPagination;
    orderBy: string;
    orderDirection: 'ASC' | 'DESC';
}

@Injectable({
    providedIn: 'root',
})
export class ControlDusStateService {
    private readonly defaultState: ControlDusState = {
        filters: {
            clienteId: null,
            temporadaId: null,
            nave: null,
            dateFrom: null,
            dateTo: null,
            referenciaCliente: '',
            contenedor: '',
            dus: '',
            booking: '',
            etapa: null,
            estado: null,
        },
        pagination: { page: 1, limit: 10 },
        orderBy: 'etd',
        orderDirection: 'DESC',
    };

    private stateSubject = new BehaviorSubject<ControlDusState>(
        this.defaultState
    );

    getCurrentState(): ControlDusState {
        return this.stateSubject.value;
    }

    updateState(partial: Partial<ControlDusState>): void {
        const next: ControlDusState = {
            ...this.stateSubject.value,
            ...partial,
            filters: {
                ...this.stateSubject.value.filters,
                ...(partial.filters || {}),
            },
            pagination: {
                ...this.stateSubject.value.pagination,
                ...(partial.pagination || {}),
            },
        };
        this.stateSubject.next(next);
    }

    clearState(): void {
        this.stateSubject.next(this.defaultState);
    }

    stateToQueryParams(state: ControlDusState): Record<string, string> {
        const p: Record<string, string> = {};
        const f = state.filters;
        if (f['clienteId'] != null) p['clienteId'] = String(f['clienteId']);
        if (f['temporadaId'] != null)
            p['temporadaId'] = String(f['temporadaId']);
        if (f['nave']) p['nave'] = String(f['nave']);
        if (f['dateFrom'])
            p['dateFrom'] = formatDateForApi(
                f['dateFrom'] instanceof Date
                    ? f['dateFrom']
                    : new Date(f['dateFrom'] as string)
            );
        if (f['dateTo'])
            p['dateTo'] = formatDateForApi(
                f['dateTo'] instanceof Date
                    ? f['dateTo']
                    : new Date(f['dateTo'] as string)
            );
        if (f['referenciaCliente'])
            p['referenciaCliente'] = f['referenciaCliente']!;
        if (f['contenedor']) p['contenedor'] = f['contenedor']!;
        if (f['dus']) p['dus'] = f['dus']!;
        if (f['booking']) p['booking'] = f['booking']!;
        if (f['etapa']) p['etapa'] = String(f['etapa']);
        if (f['estado']) p['estado'] = String(f['estado']);
        if (state.pagination['page'] !== 1)
            p['page'] = String(state.pagination['page']);
        if (state.pagination['limit'] !== 10)
            p['limit'] = String(state.pagination['limit']);
        if (state['orderBy'] !== 'etd') p['orderBy'] = state['orderBy'];
        if (state['orderDirection'] !== 'DESC')
            p['orderDirection'] = state['orderDirection'];
        return p;
    }

    queryParamsToState(params: Record<string, string>): ControlDusState {
        return {
            filters: {
                clienteId: params['clienteId'] ? +params['clienteId'] : null,
                temporadaId: params['temporadaId']
                    ? +params['temporadaId']
                    : null,
                nave: params['nave'] || null,
                dateFrom: params['dateFrom']
                    ? parseDateFromApi(params['dateFrom'])
                    : null,
                dateTo: params['dateTo'] ? parseDateFromApi(params['dateTo']) : null,
                referenciaCliente: params['referenciaCliente'] || '',
                contenedor: params['contenedor'] || '',
                dus: params['dus'] || '',
                booking: params['booking'] || '',
                etapa: params['etapa'] || null,
                estado: params['estado'] || null,
            },
            pagination: {
                page: params['page'] ? +params['page'] : 1,
                limit: params['limit'] ? +params['limit'] : 10,
            },
            orderBy: params['orderBy'] || 'etd',
            orderDirection: (params['orderDirection'] === 'ASC'
                ? 'ASC'
                : 'DESC') as 'ASC' | 'DESC',
        };
    }
}
